import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons/faCircleInfo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { CorePermissions } from "app/appPermissions";
import { MprcIconButtonDropdown, MprcText } from "components";
import { MprcFilterBox, MprcFilterCountInfo, MprcFilterIconButton, MprcFilterLine } from "components/filter";
import { MprcFilterNoRecords } from "components/filter/MprcFilterNoRecords";
import { IconAdd, IconSort } from "components/icons";
import { MprcDropdown } from "components/MprcDropdown";
import { MprcSearchTextField } from "components/MprcSearchTextField";
import { MprcTooltip } from "components/MprcTooltip";
import { CoreFilterCommand, FeedTopicHeaderResponse, FeedType, KnowledgeFeedOrder, KnowledgeQuickFilterType, PublicationType, QuickFilterDto, SavePublicationResultDto, StickerAppliesTo } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AdvancedFilter, IAdvFilterCommand, IAdvFilterDropdown, IAdvFilterMenuItem } from "views/general/AdvancedFilter";
import { changeQuickFilter, clearAdvFilter, getClearFilter } from "views/knowledge/feed/components/FeedFilter/FeedFilterCommon";
import { publicationFactory } from "views/knowledge/feed/FeedHelpers";
import { containsMenuOptions, IFeedFilterOptions, orderMenuOptions, othersMenuOptions, periodMenuOptions, sourceTopicMenuOptions, stickersMenuOptions } from "../components/FeedFilter";
import { QuickFilter } from "../components/FeedFilter/QuickFilter";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    count?: number;
    filter: IFeedFilterOptions;
    expandedAdvFilter: boolean;
    topic: FeedTopicHeaderResponse;
    onFilterChange: (filter: IFeedFilterOptions) => void;
    onGetQuickFilterOptions: () => Promise<QuickFilterDto[]>;
    onSelectedExpandedAdvFilter: (selected: boolean) => void;
    onError: (error: any) => void;
}


/**
 * Filtro Rápidos para o feed de Tópico.
 * @param props
 */
export const FeedTopicFilter: React.FC<IProps> = (props) => {

    const { topic, count, expandedAdvFilter, filter, onFilterChange, onGetQuickFilterOptions, onSelectedExpandedAdvFilter, onError } = props;

    const api = useCoreApi();

    const { permissionExplorer } = useSession();
    const { t } = useTranslation("knowledge");

    const [dialog, setDialog] = useState<any>();
    const [stickers, setStickers] = useState<IAdvFilterCommand[]>([]);
    const [dropdownOptions, setDropdownOptions] = useState<IAdvFilterDropdown[]>(getDropdownOptions());
    const orderOptions = useRef<IAdvFilterMenuItem<KnowledgeFeedOrder>[]>(orderMenuOptions(t));

    ///

    const isFiltered = filter.advFilters.length > 0 || (filter.searchText != undefined && filter.searchText.length > 0);
    const isEmptyDataset = !count && !isFiltered;

    ///
    function getDropdownOptions() {
        return [
            sourceTopicMenuOptions(t),
            containsMenuOptions(t),
            periodMenuOptions(t),
            stickersMenuOptions(t, stickers),
            othersMenuOptions(t)
        ];
    }

    /// Ao carregar o menu de rótulos
    const getStickers = (): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            api.v1.selectors.stickerAdvFilter({ applyTo: StickerAppliesTo.Publication })
                .then(result => {
                    const filterCommands: IAdvFilterCommand[] = result.data?.map(sticker => {
                        const cmd: IAdvFilterCommand = {
                            command: CoreFilterCommand.KnowledgeSticker,
                            displayName: sticker.name,
                            canRepeat: true,
                            group: 1,
                            fixedValue: sticker.stickerId,
                            quickFilterType: KnowledgeQuickFilterType.CustomSticker,
                            canCheck: true
                        };
                        return cmd;
                    });
                    setStickers(filterCommands);
                })
                .catch(error => {
                    if (onError) onError(error);
                })
                .finally(() => resolve(true));
        });
    };

    ///
    const handleClose = (newPub: SavePublicationResultDto | null) => {
        setDialog(null);
    };

    ///
    const handleNewPublication = (publicationType: PublicationType) => {
        setDialog(publicationFactory(
            null,
            publicationType,
            handleClose,
            {
                topicId: topic.topicId
            }
        ));
    };

    ////////////////////////////////////////////////////////////////////////////
    /// Effects
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        setDropdownOptions(getDropdownOptions());
    }, [stickers]);

    ////////////////////////////////////////////////////////////////////////////
    /// Render
    ////////////////////////////////////////////////////////////////////////////

    const endActions = (!isEmptyDataset &&
        <div>
            <MprcText color="muted">
                <MprcFilterIconButton
                    filtered={expandedAdvFilter || filter.advFilters.length > 0}
                    onClick={() => onSelectedExpandedAdvFilter(!expandedAdvFilter)}
                />
            </MprcText>

            <MprcIconButtonDropdown
                menuItemCheck={true}
                icon={<IconSort />}
                title={t("shared:Filter.OrderBy")}
                selected={filter.order}
                items={orderOptions.current}
                onChange={(order) => onFilterChange({ ...filter, order })}
            />
        </div>
    );

    return (
        <Box>
            {dialog}

            {!isEmptyDataset &&
                <QuickFilter
                    feedType={FeedType.Topic}
                    selectedCommands={filter.advFilters}
                    onChange={(selected, quickFilter) => onFilterChange(changeQuickFilter(selected, quickFilter, dropdownOptions, filter))}
                    onGetOptions={onGetQuickFilterOptions}
                    onError={(error => onError(error))}
                />
            }
            <Box marginTop={2}>
                {
                    expandedAdvFilter
                        ? <MprcFilterBox>
                            <AdvancedFilter
                                filter={filter}
                                leftDropdowns={dropdownOptions}
                                endComponent={endActions}
                                onGetStickers={getStickers}
                                quickFilter={{
                                    command: CoreFilterCommand.KnowledgeQuickFilter,
                                    onRemoveQuickFilter: (item) => onFilterChange(clearAdvFilter(item, filter))
                                }}
                                onFilterChange={(filter) => onFilterChange(filter)}
                                onError={error => onError(error)}
                            />
                        </MprcFilterBox>
                        : <MprcFilterBox>
                            <MprcFilterLine>
                                <div>
                                    {
                                        permissionExplorer.hasOne([CorePermissions.KnowledgePost])
                                            ? <MprcDropdown
                                                text={t("Feed.Topic.AddButtonText")}
                                                title={t("Feed.Topic.AddButtonTitle")}
                                                startIcon={<IconAdd fixedWidth />}
                                                items={
                                                    [
                                                        { text: t("Enums.PublicationType.Post", { ns: "core" }), value: PublicationType.Post, group: 1 },
                                                        { text: t("Enums.PublicationType.Question", { ns: "core" }), value: PublicationType.Question, group: 1 },
                                                        { text: t("Enums.PublicationType.Poll", { ns: "core" }), value: PublicationType.Poll, group: 1 },
                                                        { text: t("Enums.PublicationType.List", { ns: "core" }), value: PublicationType.List, group: 1 }
                                                    ]
                                                }
                                                onChange={handleNewPublication}
                                            />
                                            : <Typography variant="h3">{t("Feed.Topic.Filter.TitleView")}</Typography>
                                    }
                                </div>
                                {endActions}
                            </MprcFilterLine>

                            <Grid container>
                                <Grid size="grow">
                                    <MprcSearchTextField
                                        disabled={isEmptyDataset}
                                        initialValue={filter.searchText ?? ""}
                                        placeholder={t("Feed.Topic.SearchTextPlaceholder")}
                                        onConfirm={(value) => onFilterChange({ ...filter, searchText: value })}
                                    />
                                </Grid>
                            </Grid>
                        </MprcFilterBox>
                }
            </Box>
            {count == 0
                ? <Box mt={3}>
                    <MprcFilterNoRecords
                        message={t(isEmptyDataset ? "Feed.Topic.EmptyDatasetMessage" : "Feed.AdvancedFilter.NoRecordsFoundText")}
                        filtered={isFiltered}
                        onResetFilter={() => onFilterChange(getClearFilter())}
                    />
                </Box>
                : count != undefined &&
                <Stack direction="row" spacing={2} alignItems="center" mt={1}>
                    <MprcFilterCountInfo
                        variant={isFiltered ? "bodyNotice" : "caption"}
                        text={t(isFiltered ? "Feed.Topic.FoundPublicationMessage" : "Feed.Topic.PublicationMessage", { count: count! })}
                        onResetFilter={isFiltered ? () => onFilterChange(getClearFilter()) : undefined}
                    />
                    {topic.publicationCount > 0 &&
                        <MprcTooltip light arrow placement="right-start" title={t("Publication.Literals.PublicationViewRestrictedText")}>
                            <Box sx={{ ml: 1 }}>
                                <FontAwesomeIcon icon={faCircleInfo}></FontAwesomeIcon>
                            </Box>
                        </MprcTooltip>
                    }
                </Stack>
            }
        </Box >
    );
};
